import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCurrentUser } from './AuthComponents';
import { useStudentProgress, useDiscussionQuestions, useExperiment, useRetakeExperiment } from '../utils/convexHooks';
import { Id } from '../convex/_generated/dataModel';
import { ArrowLeft, CheckCircle, Clock, BookOpen, User, Star, Activity } from 'lucide-react';

export default function StudentResults() {
    const [searchParams] = useSearchParams();
    const idStr = searchParams.get('id');
    const experimentId = idStr ? (idStr as Id<"experimentConfigs">) : undefined;
    const navigate = useNavigate();
    const { user } = useCurrentUser();
    const retakeExperiment = useRetakeExperiment();
    
    // local loading state for retake button
    const [isRetaking, setIsRetaking] = useState(false);

    const experimentConfig = useExperiment(experimentId);
    const progress = useStudentProgress(user?._id, experimentId);
    const discussionQuestions = useDiscussionQuestions(experimentId);

    if (!user || !experimentId) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <p className="text-xl text-gray-700">Missing experiment ID or User</p>
                <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg">Back to Dashboard</button>
            </div>
        );
    }

    if (progress === undefined || experimentConfig === undefined) {
        return (
             <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!experimentConfig) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <p className="text-xl text-gray-700">Experiment not found.</p>
                <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg">Back to Dashboard</button>
            </div>
        );
    }

    if (progress?.status !== 'completed') {
         return (
             <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <p className="text-xl text-gray-700">No completed results found for this experiment.</p>
                <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg">Back to Dashboard</button>
            </div>
        );
    }

    const { labData, postLab: discussionData } = progress;
    const breakdown = labData?.dataPoints?.[0]?.breakdown;
    
    // Calculate total score exactly as the teacher view does
    const labScore = labData?.dataPoints && labData.dataPoints.length > 0
        ? labData.dataPoints[0].algorithmicScore || 0
        : 0;
    
    // Decide if it needs manual review
    const isNeedsManualReview = discussionQuestions && discussionQuestions.length > 0 && discussionData && discussionData.grade === undefined;
    
    // Calculate discussion score
    const maxDiscussionScore = discussionQuestions?.length ? discussionQuestions.length * 100 : 0;
    let studentDiscussionScore = 0;
    if (discussionData?.answers) {
         studentDiscussionScore = discussionData.answers.reduce((acc: number, ans: any) => acc + (ans.autoGradeScore || 0), 0);
    }
    const discussionPercentage = maxDiscussionScore > 0 ? (studentDiscussionScore / maxDiscussionScore) * 100 : 100;
    
    // Final composite (Teacher logic defaults: Lab=60%, Discussion=40%)
    const labWeight = 0.6;
    const discWeight = 0.4;
    const finalCalculatedScore = Math.round((labScore * labWeight) + ((discussionPercentage || 0) * discWeight));

    return (
        <div className="min-h-screen bg-[#f8fafc] dark:bg-[#030712] py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-500 font-sans">
            <div className="max-w-[1400px] mx-auto space-y-6">
                
                {/* Header Navigation */}
                <button 
                    onClick={() => navigate('/student/dashboard')}
                    className="group inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl bg-white dark:bg-[#0B1120] text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-800 shadow-sm transition-all duration-300"
                >
                    <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
                    Return to Dashboard
                </button>

                {/* Dashboard Container */}
                <div className="flex flex-col gap-6">
                    
                    {/* -- TOP BANNER: Title & Status -- */}
                    <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-[#0B1120] border border-slate-200 dark:border-slate-800 p-8 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
                        {/* Subtle background mesh/glow */}
                        {isNeedsManualReview ? (
                            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-yellow-500/10 dark:bg-yellow-500/5 blur-3xl rounded-full pointer-events-none translate-x-1/2 -translate-y-1/2"></div>
                        ) : (
                            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-indigo-500/10 dark:bg-indigo-500/5 blur-3xl rounded-full pointer-events-none translate-x-1/2 -translate-y-1/2"></div>
                        )}
                        
                        <div className="relative z-10 flex flex-col gap-2">
                            <div className="flex items-center gap-3">
                                <div className={`inline-flex items-center justify-center p-2 rounded-lg ${isNeedsManualReview ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'}`}>
                                    {isNeedsManualReview ? <Clock className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                                </div>
                                <span className={`text-sm font-bold uppercase tracking-wider ${isNeedsManualReview ? 'text-yellow-600 dark:text-yellow-500' : 'text-emerald-600 dark:text-emerald-500'}`}>
                                    {isNeedsManualReview ? 'Pending Manual Review' : 'Evaluation Complete'}
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
                                {experimentConfig.title}
                            </h1>
                            <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-xl mt-1">
                                {isNeedsManualReview 
                                    ? "Your teacher must manually review your discussion answers before releasing the final grade computation." 
                                    : "Comprehensive telemetry and post-lab analysis have been successfully compiled."}
                            </p>
                        </div>
                    </div>

                    {/* -- ROW 2: The Core Metrics (Gutenberg Focus) -- */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                        {/* Simulation Lab Metric */}
                        <div className="bg-white dark:bg-[#0B1120] border border-slate-200 dark:border-slate-800 rounded-3xl p-8 flex flex-col justify-between shadow-sm relative overflow-hidden group">
                            <div className="absolute -inset-1 bg-gradient-to-br from-indigo-500/0 to-blue-500/0 group-hover:from-indigo-500/10 group-hover:to-blue-500/5 transition-colors duration-500 rounded-3xl pointer-events-none"></div>
                            <div className="flex justify-between items-start mb-6">
                                <div className="space-y-1">
                                    <div className="text-xs font-bold text-slate-400 tracking-widest uppercase">Simulation Telemetry</div>
                                    <div className="text-sm font-medium text-slate-500 dark:text-slate-500">60% Weight in Final</div>
                                </div>
                                <div className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                                    <BookOpen className="w-6 h-6" />
                                </div>
                            </div>
                            <div>
                                <div className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-2">{labScore}%</div>
                                <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-indigo-500 rounded-full transition-all duration-1000" style={{ width: `${labScore}%` }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Final Composite Metric (The Big One) */}
                        <div className={`border rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-lg relative overflow-hidden transition-all
                            ${isNeedsManualReview ? "bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-700/50" : "bg-gradient-to-br from-slate-900 to-indigo-950 dark:from-[#090E17] dark:to-[#12102A] border-indigo-500/30"}`}>
                            {/* Inner Glow */}
                            {!isNeedsManualReview && (
                                <div className="absolute inset-0 flex items-center justify-center opacity-50 pointer-events-none">
                                    <div className="w-48 h-48 bg-indigo-500/30 blur-[60px] rounded-full"></div>
                                </div>
                            )}
                            
                            <div className="relative z-10 space-y-4 flex flex-col items-center">
                                <div className={`px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-full border ${isNeedsManualReview ? "bg-yellow-100/50 border-yellow-300 text-yellow-700 dark:text-yellow-500" : "bg-indigo-500/20 border-indigo-400/30 text-indigo-300"}`}>
                                    Final Composite Grade
                                </div>
                                <div className={`text-6xl md:text-7xl font-black tracking-tighter ${isNeedsManualReview ? "text-yellow-600 dark:text-yellow-500" : "text-white"}`}>
                                    {isNeedsManualReview ? "Pending" : `${finalCalculatedScore}%`}
                                </div>
                            </div>
                        </div>

                        {/* Discussion Metric */}
                        <div className="bg-white dark:bg-[#0B1120] border border-slate-200 dark:border-slate-800 rounded-3xl p-8 flex flex-col justify-between shadow-sm relative overflow-hidden group">
                             <div className="absolute -inset-1 bg-gradient-to-bl from-purple-500/0 to-fuchsia-500/0 group-hover:from-purple-500/10 group-hover:to-fuchsia-500/5 transition-colors duration-500 rounded-3xl pointer-events-none"></div>
                            <div className="flex justify-between items-start mb-6">
                                <div className="space-y-1">
                                    <div className="text-xs font-bold text-slate-400 tracking-widest uppercase">Post-Lab Discussion</div>
                                    <div className="text-sm font-medium text-slate-500 dark:text-slate-500">40% Weight in Final</div>
                                </div>
                                <div className="p-3 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
                                    <User className="w-6 h-6" />
                                </div>
                            </div>
                            <div>
                                <div className={`text-4xl md:text-5xl font-black mb-2 ${isNeedsManualReview ? "text-slate-400" : "text-slate-900 dark:text-white"}`}>
                                    {isNeedsManualReview ? "—" : `${Math.round(discussionPercentage)}%`}
                                </div>
                                <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                     <div className={`h-full rounded-full transition-all duration-1000 ${isNeedsManualReview ? 'bg-slate-300' : 'bg-purple-500'}`} style={{ width: `${isNeedsManualReview ? 0 : discussionPercentage}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* -- ROW 3: Granular Diagnostics Array -- */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        
                        {/* Left Col: Simulation Diagnostics (5 cols) */}
                        <div className="lg:col-span-4 flex flex-col bg-white dark:bg-[#0B1120] border border-slate-200 dark:border-slate-800 rounded-3xl p-6 md:p-8 shadow-sm">
                            <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100 dark:border-slate-800/80">
                                <div className="w-2 h-6 bg-indigo-500 rounded-full"></div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Diagnostics Breakdown</h3>
                            </div>
                            
                            <div className="flex-1 space-y-6">
                                {breakdown ? (
                                    <>
                                        {/* Row 1: Concentration */}
                                        <div className="group">
                                            <div className="flex justify-between items-end mb-2">
                                                <div>
                                                    <div className="font-semibold text-slate-900 dark:text-slate-200">Concentration Accuracy</div>
                                                    <div className="text-xs text-slate-400 font-medium">Variance from true value</div>
                                                </div>
                                                <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{breakdown.concentrationAccuracy}<span className="text-sm font-medium text-slate-400">/40</span></div>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500 group-hover:bg-indigo-400 transition-colors" style={{ width: `${(breakdown.concentrationAccuracy/40)*100}%` }}></div>
                                            </div>
                                        </div>

                                        {/* Row 2: Data Quality */}
                                        <div className="group">
                                            <div className="flex justify-between items-end mb-2">
                                                <div>
                                                    <div className="font-semibold text-slate-900 dark:text-slate-200">Data Quality</div>
                                                    <div className="text-xs text-slate-400 font-medium">Volume & spread of plot points</div>
                                                </div>
                                                <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{breakdown.dataQuality}<span className="text-sm font-medium text-slate-400">/30</span></div>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500 group-hover:bg-indigo-400 transition-colors" style={{ width: `${(breakdown.dataQuality/30)*100}%` }}></div>
                                            </div>
                                        </div>

                                        {/* Row 3: Technique */}
                                        <div className="group">
                                            <div className="flex justify-between items-end mb-2">
                                                <div>
                                                    <div className="font-semibold text-slate-900 dark:text-slate-200">Equipment Technique</div>
                                                    <div className="text-xs text-slate-400 font-medium">Handling and overshoot control</div>
                                                </div>
                                                <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{breakdown.techniqueScore}<span className="text-sm font-medium text-slate-400">/20</span></div>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500 group-hover:bg-indigo-400 transition-colors" style={{ width: `${(breakdown.techniqueScore/20)*100}%` }}></div>
                                            </div>
                                        </div>

                                        {/* Row 4: Time Bonus */}
                                        <div className="group">
                                            <div className="flex justify-between items-end mb-2">
                                                <div>
                                                    <div className="font-semibold text-slate-900 dark:text-slate-200 text-yellow-600 dark:text-yellow-500">Expedition Bonus</div>
                                                    <div className="text-xs text-slate-400 font-medium">Speed threshold modifier</div>
                                                </div>
                                                <div className="text-lg font-bold text-yellow-500">{breakdown.timeBonus}<span className="text-sm font-medium text-slate-400">/10</span></div>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-yellow-500 transition-colors" style={{ width: `${(breakdown.timeBonus/10)*100}%` }}></div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                        <Activity className="w-8 h-8 mb-2 opacity-50" />
                                        <span className="text-sm font-medium">No granular telemetry tracked</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Col: AI Discussion Evaluation (8 cols) */}
                        <div className="lg:col-span-8 flex flex-col bg-white dark:bg-[#0B1120] border border-slate-200 dark:border-slate-800 rounded-3xl p-6 md:p-8 shadow-sm">
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100 dark:border-slate-800/80">
                                <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Automated QA Analysis</h3>
                            </div>

                            <div className="flex-1 space-y-6 overflow-y-auto pr-2 custom-scrollbar" style={{ maxHeight: '600px' }}>
                                {discussionData?.answers && discussionQuestions ? (
                                    discussionQuestions.map((q, idx) => {
                                        const ans = discussionData.answers.find((a: any) => a.questionId === q._id);
                                        const score = ans?.autoGradeScore || 0;
                                        const pendingReview = ans?.autoGradeFeedback?.toLowerCase().includes("manual review") || isNeedsManualReview;
                                        
                                        // Color system based on AI evaluation
                                        const isGreat = score >= 80 && !pendingReview;
                                        const isMid = score >= 50 && score < 80 && !pendingReview;
                                        const isPoor = score < 50 && !pendingReview;
                                        
                                        return (
                                            <div key={q._id} className="relative pl-6 lg:pl-8 border-l border-slate-200 dark:border-slate-800 ml-2 pb-2">
                                                {/* Timeline dot */}
                                                <div className={`absolute top-0 left-[-6px] w-3 h-3 rounded-full border-2 border-white dark:border-[#0B1120] 
                                                    ${pendingReview ? 'bg-yellow-500' : isGreat ? 'bg-emerald-500' : isMid ? 'bg-blue-500' : 'bg-red-500'}`}>
                                                </div>

                                                <div className="bg-slate-50 dark:bg-[#121827] rounded-2xl p-5 md:p-6 mb-2 border border-slate-100 dark:border-slate-800/80 transition-all hover:border-slate-300 dark:hover:border-slate-700">
                                                    <div className="flex items-start justify-between gap-4 mb-4">
                                                        <h4 className="text-base font-bold text-slate-900 dark:text-slate-100 leading-snug">
                                                            <span className="text-purple-500 font-black mr-2">Q{idx + 1}.</span>
                                                            {q.question}
                                                        </h4>
                                                        {/* Score Badge */}
                                                        <div className={`shrink-0 flex items-center justify-center px-3 py-1 rounded-lg text-sm font-bold border
                                                            ${pendingReview ? 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-500 border-yellow-200 dark:border-yellow-500/20' :
                                                              isGreat ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-200 dark:border-emerald-500/20' :
                                                              isMid ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-500 border-blue-200 dark:border-blue-500/20' :
                                                              'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-500 border-red-200 dark:border-red-500/20'}`}>
                                                            {pendingReview ? 'REV' : `${score}/100`}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Student Answer */}
                                                    <div className="mb-5">
                                                        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-2">Student Submission</div>
                                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 border-l-2 border-purple-500 pl-4 italic">
                                                            {ans?.answer ? `"${ans.answer}"` : "No execution submitted"}
                                                        </p>
                                                    </div>

                                                    {/* AI Feedback */}
                                                    <div className="bg-white dark:bg-[#0B1120] rounded-xl p-4 border border-slate-100 dark:border-slate-800">
                                                        <div className="text-[11px] flex flex-row items-center uppercase tracking-widest font-bold text-slate-400 mb-2">
                                                            <Star className="w-3 h-3 mr-1" /> AI Engine Feedback
                                                        </div>
                                                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                                            {ans?.autoGradeFeedback || "Feedback pending system evaluation."}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="flex flex-col items-center justify-center p-12 text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                        <BookOpen className="w-8 h-8 mb-2 opacity-50" />
                                        <span className="text-sm font-medium">No discussion data received into the engine.</span>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                {/* Optional Teacher Controls Area / Bottom Bar */}
                <div className="w-full flex justify-end">
                            {experimentConfig?.maxRetakes === 'unlimited' || progress?.retakesCount === undefined || (experimentConfig?.maxRetakes && experimentConfig.maxRetakes > progress.retakesCount) ? (
                                <button
                                    onClick={async () => {
                                        setIsRetaking(true);
                                        try {
                                            await retakeExperiment({ studentId: user._id, experimentConfigId: experimentId });
                                            let path = '/play';
                                            if (experimentConfig.experimentType === 'znH2SO4') path = `/play/experiment/zinc-h2so4?id=${experimentConfig._id}`;
                                            else if (experimentConfig.experimentType === 'titration') path = `/play/experiment/titration-hcl-naoh?id=${experimentConfig._id}`;
                                            else if (experimentConfig.experimentType === 'naphthalene') path = `/play/experiment/naphthalene?id=${experimentConfig._id}`;
                                            else if (experimentConfig.experimentType === 'solubility') path = `/play/experiment/solubility?id=${experimentConfig._id}`;
                                            else if (experimentConfig.experimentType === 'magnesiumOxide') path = `/play/experiment/magnesium-oxide?id=${experimentConfig._id}`;
                                            else if (experimentConfig.experimentType === 'copperOxide') path = `/play/experiment/copper-oxide?id=${experimentConfig._id}`;
                                            else if (experimentConfig.experimentType === 'balancedEquations') path = `/play/experiment/balanced-equations?id=${experimentConfig._id}`;
                                            navigate(path);
                                        } catch (e: any) {
                                            alert(e.message || "Failed to retake experiment");
                                            setIsRetaking(false);
                                        }
                                    }}
                                    disabled={isRetaking}
                                    className="px-6 py-3 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 font-bold rounded-xl hover:bg-red-100 dark:hover:bg-red-500/20 transition-all shadow-sm border border-red-200 dark:border-red-500/20"
                                >
                                    {isRetaking ? 'Restarting...' : 'Retake Experiment'}
                                </button>
                            ) : null}
                            <button
                                onClick={() => navigate('/student/dashboard')}
                                className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-500/20 transition-all"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>
    );
}
